import React, { lazy, Suspense, useCallback, useRef } from 'react';
import {
  BrowserRouter as Router,
  Switch,
  Route,
  Redirect,
} from 'react-router-dom';
import { CssBaseline, ThemeProvider, IconButton } from '@material-ui/core';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import moment from 'moment';
import MomentUtils from '@date-io/moment';
import { SnackbarProvider } from 'notistack';
import { Close as CloseIcon } from '@material-ui/icons';

import NestingRoute from './routes/NestingRoute';
import PrivateRoute from './routes/PrivateRoute';
import { CircularProgress } from './components';
import { theme } from './utils';
import { AuthProvider } from './contexts/AuthContext';

const PageNotFound = lazy(() => import('./routes/pageNotFound'));
const LoginPage = lazy(() => import('./routes/login'));
const HomePage = lazy(() => import('./routes/home'));
// Clients Page Group
const clientsListPage = lazy(() => import('./routes/clients/list'));
const clientShowPage = lazy(() => import('./routes/clients/show'));
// Cash In Page Group
const CashInListPage = lazy(() => import('./routes/cashIn/list'));
const CashInShowPage = lazy(() => import('./routes/cashIn/show'));
const CashInEditPage = lazy(() => import('./routes/cashIn/edit'));
// Cash Out Page Group
const CashOutListPage = lazy(() => import('./routes/cashOut/list'));
const CashOutShowPage = lazy(() => import('./routes/cashOut/show'));
const CashOutEditPage = lazy(() => import('./routes/cashOut/edit'));
// Swaps Page Group
const SwapsListPage = lazy(() => import('./routes/swaps/list'));
const SwapShowPage = lazy(() => import('./routes/swaps/show'));
const SwapEditPage = lazy(() => import('./routes/swaps/edit'));
// Quotations Page Group
const QuotationsCreatePage = lazy(() => import('./routes/quotations/create'));
// Payments
const PaymentsListPage = lazy(() => import('./routes/payments/list'));
const PaymentShowPage = lazy(() => import('./routes/payments/show'));
// Merchats
const MerchantsListPage = lazy(() => import('./routes/merchants/list'));
const MerchantsShowPage = lazy(() => import('./routes/merchants/view'));
// Retirements
const RetirementsListPage = lazy(() => import('./routes/retirements/list'));
const RetirementShowPage = lazy(() => import('./routes/retirements/show'));
// Tasks
const TasksListPage = lazy(() => import('./routes/tasks/list'));
// KriptoStore
const KriptoStoreListPage = lazy(() => import('./routes/kriptostore/list'));
const KriptoStoreViewPage = lazy(() => import('./routes/kriptostore/view'));
const KriptoStoreEditPage = lazy(() => import('./routes/kriptostore/edit'));
// KriptoPayment
const KriptoPaymentListPage = lazy(() => import('./routes/kriptopayment/list'));
const KriptoPaymentShowPage = lazy(() => import('./routes/kriptopayment/show'));
const KriptoPaymentEditPage = lazy(() => import('./routes/kriptopayment/edit'));
// Banks group page
const BanksListPage = lazy(() => import('./routes/banks/list'));
const BankCreatePage = lazy(() => import('./routes/banks/create'));
const BankShowPage = lazy(() => import('./routes/banks/show'));
const BankEditPage = lazy(() => import('./routes/banks/edit'));

// Carousels
const CarouselsPage = lazy(() => import('./routes/carousels'));

// Reports
const ReportsCreatePage = lazy(() => import('./routes/reports/create'));
const ReportHistoryPage = lazy(() => import('./routes/reports/history'));

// Wallets Payment
const WalletsPaymentListPage = lazy(() =>
  import('./routes/walletsPayment/list'),
);

// Devolutions
const DevolutionsList = lazy(() => import('./routes/devolutions/list'));
const DevolutionsCreate = lazy(() => import('./routes/devolutions/create'));
const DevolutionsView = lazy(() => import('./routes/devolutions/show'));
const DevolutionsEdit = lazy(() => import('./routes/devolutions/edit'));

// Services Config
const ServicesConfigList = lazy(() => import('./routes/servicesConfig/list'));
const ServicesConfigItem = lazy(() =>
  import('./routes/servicesConfig/serviceItem'),
);
const ServicesConfigAdd = lazy(() => import('./routes/servicesConfig/add'));

// Dashboard
const DashboardPage = lazy(() => import('./routes/dashboard'));

// Rewards
const RewardsListPage = lazy(() => import('./routes/rewards/list'));
const RewardsCreatePage = lazy(() => import('./routes/rewards/create'));
const RewardShowPage = lazy(() => import('./routes/rewards/show'));

// Indexers
const IndexersList = lazy(() => import('./routes/indexers'));

// ToUp
const ToUpList = lazy(() => import('./routes/toUp/list'));
const ToUpShow = lazy(() => import('./routes/toUp/show'));

// CryptoNetworks
const CryptoNetworksList = lazy(() => import('./routes/cryptoNetworks/list'));
const CryptoNetworkShow = lazy(() => import('./routes/cryptoNetworks/show'));
const CryptoNetworkEdit = lazy(() => import('./routes/cryptoNetworks/edit'));

// Deposits
const DepositsList = lazy(() => import('./routes/deposits/list'));
const DepositShow = lazy(() => import('./routes/deposits/show'));

// Transfers
const TransfersList = lazy(() => import('./routes/transfers/list'));
const TransferShow = lazy(() => import('./routes/transfers/show'));

// Subsidies
const SubsidiesList = lazy(() => import('./routes/subsidies/list'));

// Balances
const BalancesList = lazy(() => import('./routes/balances/list'));
const BalanceGraph = lazy(() => import('./routes/balances/graph'));

// Kriptonians
const KriptoniansList = lazy(() => import('./routes/kriptonians/list'));
const KriptonianShow = lazy(() => import('./routes/kriptonians/show'));

// Collector Accounts
const CollectorAccountsList = lazy(() =>
  import('./routes/collectorAccounts/list'),
);
const CollectorAccountShow = lazy(() =>
  import('./routes/collectorAccounts/show'),
);

// Admins
const AdminsListPage = lazy(() => import('./routes/admins/list'));
const AdminShowPage = lazy(() => import('./routes/admins/show'));

const hideDurationSnackbars = 5000; // 5 segundos

const App = () => {
  const notistackRef = useRef(null);

  const handleCloseSnackbar = useCallback(
    (key) => () => {
      notistackRef.current.closeSnackbar(key);
    },
    [],
  );

  return (
    <ThemeProvider theme={theme}>
      <AuthProvider>
        <MuiPickersUtilsProvider libInstance={moment} utils={MomentUtils}>
          <SnackbarProvider
            ref={notistackRef}
            maxSnack={1}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            autoHideDuration={hideDurationSnackbars}
            preventDuplicate
            action={(keySnackbar) => (
              <IconButton
                size="small"
                onClick={handleCloseSnackbar(keySnackbar)}
              >
                <CloseIcon />
              </IconButton>
            )}
          >
            <div>
              <CssBaseline />
              <Router>
                <Suspense
                  fallback={<CircularProgress style={{ minHeight: '100vh' }} />}
                >
                  <Switch>
                    <Route
                      exact
                      path="/"
                      component={() => <Redirect to="/login" />}
                    />

                    <Route exact path="/login" component={LoginPage} />

                    <PrivateRoute exact path="/home" component={HomePage} />

                    <NestingRoute
                      path="/clients"
                      component={clientsListPage}
                      isPrivate
                      section="clients"
                      nesting={[
                        {
                          path: '/:id',
                          component: clientShowPage,
                          exact: true,
                        },
                        {
                          path: '*',
                          component: PageNotFound,
                          exact: false,
                        },
                      ]}
                    />

                    <NestingRoute
                      path="/merchants"
                      component={MerchantsListPage}
                      isPrivate
                      section="merchants"
                      nesting={[
                        {
                          path: '/:id',
                          component: MerchantsShowPage,
                          exact: true,
                        },
                        {
                          path: '*',
                          component: PageNotFound,
                          exact: false,
                        },
                      ]}
                    />

                    <NestingRoute
                      path="/cash-in"
                      component={CashInListPage}
                      isPrivate
                      section="cash_in"
                      nesting={[
                        {
                          path: '/:id',
                          component: CashInShowPage,
                          exact: true,
                        },
                        {
                          path: '/:id/edit',
                          component: CashInEditPage,
                          exact: true,
                        },
                        {
                          path: '*',
                          component: PageNotFound,
                          exact: false,
                        },
                      ]}
                    />

                    <NestingRoute
                      path="/cash-out"
                      component={CashOutListPage}
                      isPrivate
                      section="cash_out"
                      nesting={[
                        {
                          path: '/:id',
                          component: CashOutShowPage,
                          exact: true,
                        },
                        {
                          path: '/:id/edit',
                          component: CashOutEditPage,
                          exact: true,
                        },
                        {
                          path: '*',
                          component: PageNotFound,
                          exact: false,
                        },
                      ]}
                    />

                    <NestingRoute
                      path="/swap"
                      component={SwapsListPage}
                      isPrivate
                      section="swaps"
                      nesting={[
                        {
                          path: '/:id',
                          component: SwapShowPage,
                          exact: true,
                        },
                        {
                          path: '/:id/edit',
                          component: SwapEditPage,
                          exact: true,
                        },
                        {
                          path: '*',
                          component: PageNotFound,
                          exact: false,
                        },
                      ]}
                    />

                    <PrivateRoute
                      path="/quotations/create"
                      component={QuotationsCreatePage}
                      section="quotations"
                      exact
                    />

                    <NestingRoute
                      path="/payments"
                      component={PaymentsListPage}
                      isPrivate
                      section="payments"
                      nesting={[
                        {
                          path: '/:id',
                          component: PaymentShowPage,
                          exact: true,
                        },
                        {
                          path: '*',
                          component: PageNotFound,
                          exact: false,
                        },
                      ]}
                    />

                    <NestingRoute
                      path="/retirements"
                      component={RetirementsListPage}
                      isPrivate
                      section="retirements"
                      nesting={[
                        {
                          path: '/:id',
                          component: RetirementShowPage,
                          exact: true,
                        },
                        {
                          path: '*',
                          component: PageNotFound,
                          exact: false,
                        },
                      ]}
                    />

                    <NestingRoute
                      path="/kriptostore"
                      component={KriptoStoreListPage}
                      isPrivate
                      section="kriptostore"
                      nesting={[
                        {
                          path: '/:id',
                          component: KriptoStoreViewPage,
                          exact: true,
                        },
                        {
                          path: '/:id/edit',
                          component: KriptoStoreEditPage,
                          exact: true,
                        },
                        {
                          path: '*',
                          component: PageNotFound,
                          exact: false,
                        },
                      ]}
                    />

                    <PrivateRoute
                      path="/tasks"
                      component={TasksListPage}
                      section="tasks"
                      exact
                    />

                    <NestingRoute
                      path="/kriptopayment"
                      component={KriptoPaymentListPage}
                      isPrivate
                      section="kriptopayment"
                      nesting={[
                        {
                          path: '/:id',
                          component: KriptoPaymentShowPage,
                          exact: true,
                        },
                        {
                          path: '/:id/edit',
                          component: KriptoPaymentEditPage,
                          exact: true,
                        },
                        {
                          path: '*',
                          component: PageNotFound,
                          exact: false,
                        },
                      ]}
                    />

                    <NestingRoute
                      path="/banks"
                      component={BanksListPage}
                      isPrivate
                      section="banks"
                      nesting={[
                        {
                          path: '/create',
                          component: BankCreatePage,
                          exact: true,
                        },
                        {
                          path: '/:id',
                          component: BankShowPage,
                          exact: true,
                        },
                        {
                          path: '/:id/edit',
                          component: BankEditPage,
                          exact: true,
                        },
                        {
                          path: '*',
                          component: PageNotFound,
                          exact: false,
                        },
                      ]}
                    />

                    <NestingRoute
                      path="/devolutions"
                      component={DevolutionsList}
                      isPrivate
                      section="devolutions"
                      nesting={[
                        {
                          path: '/create',
                          component: DevolutionsCreate,
                          exact: true,
                        },
                        {
                          path: '/:id',
                          component: DevolutionsView,
                          exact: true,
                        },
                        {
                          path: '/:id/edit',
                          component: DevolutionsEdit,
                          exact: true,
                        },
                        {
                          path: '*',
                          component: PageNotFound,
                          exact: false,
                        },
                      ]}
                    />

                    <PrivateRoute
                      exact
                      path="/carousels"
                      component={CarouselsPage}
                      section="carousels"
                    />

                    <NestingRoute
                      path="/reports"
                      component={ReportsCreatePage}
                      isPrivate
                      section="reports"
                      nesting={[
                        {
                          path: '/history',
                          component: ReportHistoryPage,
                          exact: true,
                        },
                        {
                          path: '*',
                          component: PageNotFound,
                          exact: false,
                        },
                      ]}
                    />

                    <PrivateRoute
                      path="/wallets-payment"
                      component={WalletsPaymentListPage}
                      section="wallets_payment"
                      exact
                    />

                    <NestingRoute
                      path="/services-config"
                      component={ServicesConfigList}
                      isPrivate
                      section="services_config"
                      nesting={[
                        {
                          path: '/add',
                          component: ServicesConfigAdd,
                          exact: true,
                        },
                        {
                          path: '/:service',
                          component: ServicesConfigItem,
                          exact: true,
                        },
                        {
                          path: '*',
                          component: PageNotFound,
                          exact: false,
                        },
                      ]}
                    />

                    <PrivateRoute
                      path="/dashboard"
                      component={DashboardPage}
                      section="dashboard"
                      exact
                    />

                    <NestingRoute
                      path="/rewards"
                      component={RewardsListPage}
                      isPrivate
                      section="rewards"
                      nesting={[
                        {
                          path: '/create',
                          component: RewardsCreatePage,
                          exact: true,
                        },
                        {
                          path: '/:id',
                          component: RewardShowPage,
                          exact: true,
                        },
                        {
                          path: '*',
                          component: PageNotFound,
                          exact: false,
                        },
                      ]}
                    />

                    <PrivateRoute
                      path="/indexers"
                      component={IndexersList}
                      section="indexers"
                      exact
                    />

                    <NestingRoute
                      path="/to-up"
                      component={ToUpList}
                      isPrivate
                      section="top_up"
                      nesting={[
                        {
                          path: '/:id',
                          component: ToUpShow,
                          exact: true,
                        },
                        {
                          path: '*',
                          component: PageNotFound,
                          exact: false,
                        },
                      ]}
                    />

                    <NestingRoute
                      path="/crypto-networks"
                      component={CryptoNetworksList}
                      isPrivate
                      section="crypto_networks"
                      nesting={[
                        {
                          path: '/:id',
                          component: CryptoNetworkShow,
                          exact: true,
                        },
                        {
                          path: '/:id/edit',
                          component: CryptoNetworkEdit,
                          exact: true,
                        },
                        {
                          path: '*',
                          component: PageNotFound,
                          exact: false,
                        },
                      ]}
                    />

                    <NestingRoute
                      path="/deposits"
                      component={DepositsList}
                      isPrivate
                      section="deposits"
                      nesting={[
                        {
                          path: '/:id',
                          component: DepositShow,
                          exact: true,
                        },
                        {
                          path: '*',
                          component: PageNotFound,
                          exact: false,
                        },
                      ]}
                    />

                    <NestingRoute
                      path="/transfers"
                      component={TransfersList}
                      isPrivate
                      section="transfers"
                      nesting={[
                        {
                          path: '/:id',
                          component: TransferShow,
                          exact: true,
                        },
                        {
                          path: '*',
                          component: PageNotFound,
                          exact: false,
                        },
                      ]}
                    />

                    <PrivateRoute
                      path="/subsidies"
                      component={SubsidiesList}
                      section="subsidies"
                      exact
                    />

                    <NestingRoute
                      path="/balances"
                      component={BalancesList}
                      isPrivate
                      section="balances"
                      nesting={[
                        {
                          path: '/:id/graph',
                          component: BalanceGraph,
                          exact: true,
                        },
                        {
                          path: '*',
                          component: PageNotFound,
                          exact: false,
                        },
                      ]}
                    />

                    <NestingRoute
                      path="/kriptonians"
                      component={KriptoniansList}
                      isPrivate
                      section="kriptonians"
                      nesting={[
                        {
                          path: '/:id',
                          component: KriptonianShow,
                          exact: true,
                        },
                        {
                          path: '*',
                          component: PageNotFound,
                          exact: false,
                        },
                      ]}
                    />

                    <NestingRoute
                      path="/collector-accounts"
                      component={CollectorAccountsList}
                      isPrivate
                      section="collector_accounts"
                      nesting={[
                        {
                          path: '/:id',
                          component: CollectorAccountShow,
                          exact: true,
                        },
                        {
                          path: '*',
                          component: PageNotFound,
                          exact: false,
                        },
                      ]}
                    />

                    <NestingRoute
                      path="/admins"
                      component={AdminsListPage}
                      isPrivate
                      nesting={[
                        {
                          path: '/:id',
                          component: AdminShowPage,
                          exact: true,
                        },
                        {
                          path: '*',
                          component: PageNotFound,
                          exact: false,
                        },
                      ]}
                    />

                    <Route path="*" component={PageNotFound} />
                  </Switch>
                </Suspense>
              </Router>
            </div>
          </SnackbarProvider>
        </MuiPickersUtilsProvider>
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;
